/**
 * Auction time: wall time with the pauses taken out.
 *
 * Tim, 9 September: "when I pause the farm clock, it seems to run in the background. when I hit
 * start again, the clock zeroes out and the player is awarded to whoever was leading before the
 * pause."
 *
 * WHAT WENT WRONG IS THE DIFFERENCE BETWEEN STOPPING A CLOCK AND STOPPING ITS PICTURE. Pausing the
 * farm rounds froze `clockNow` at the instant of the pause, so every figure on the screen held
 * still and the going-gone clock could not fire. It looked right. But `bidAt` is a wall-clock
 * instant and the countdown is `bidAt + 20s − now`, so the moment the room started again and `now`
 * became real again, the twenty seconds had been eaten by however long the pause was — the clock
 * read 0:00 on the first tick and the auto-hammer sold the lot to whoever happened to be leading
 * when Tim stopped the room. A pause to settle an argument decided the argument.
 *
 * The draft board has had the answer since it was written: "the clocks count DRAFT time, wall time
 * minus these intervals". `DraftState.farmPauses` is that ledger and it was already being written
 * on every pause — nothing read it except to ask whether a pause was open. So this is the reading
 * half, and it is the same arithmetic as `openPicks`, pulled out where both can hold it.
 *
 * An open interval — one with no `until` — runs to the instant being asked about, which is what
 * "the room has not started again" means to a countdown.
 */

/** A pause, as stored: ISO instants, and no `until` while the room is still stopped. */
export interface PauseSpan {
  at: string;
  until?: string | null;
}

/** Parsed, filtered of nonsense, oldest first. */
function spans(pauses: readonly PauseSpan[] | undefined) {
  return [...(pauses ?? [])]
    .map((p) => ({ at: Date.parse(p.at), until: p.until ? Date.parse(p.until) : Infinity }))
    .filter((p) => Number.isFinite(p.at))
    .sort((a, b) => a.at - b.at);
}

/**
 * How much of the wall-clock span [from, to] the room spent stopped.
 *
 * Overlapping intervals would be double-counted, and cannot arise: `setFarmStatus` closes the open
 * one before opening another. Clamping each interval to the span is what makes a pause BEFORE the
 * bid — a room stopped between two lots — cost that lot nothing.
 */
export function stoppedMs(
  pauses: readonly PauseSpan[] | undefined,
  from: number,
  to: number,
): number {
  if (!(to > from)) return 0;
  let n = 0;
  for (const s of spans(pauses)) {
    const lo = Math.max(from, s.at);
    const hi = Math.min(to, s.until);
    if (hi > lo) n += hi - lo;
  }
  return n;
}

/**
 * The same figure for a clock that started at an ISO instant. Null `since` means no clock.
 *
 * This is what a caller hands to `countdown` and `bidHasSold` as `held`, and getting it from one
 * place is the point: a going-gone clock that subtracted the pauses while the auto-hammer did not
 * would show a number the room could watch reach zero without anything happening.
 */
export function heldSince(
  pauses: readonly PauseSpan[] | undefined,
  since: string | null | undefined,
  now: number,
): number {
  if (!since) return 0;
  const at = Date.parse(since);
  return Number.isNaN(at) ? 0 : stoppedMs(pauses, at, now);
}
